/**
 * "What does this technician bring?" — prior trade background, stated by a
 * person. Optional: skipping leaves it "not provided", never "New to the
 * trade". Touch-first: big tappable choices, no hover-only controls.
 *
 * Used by setup (wizard technician rows), adding/editing a technician, the
 * owner-who-also-turns-wrenches step, and the technician's own Passport.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { EXPERIENCE_LEVELS, type ExperienceLevel } from '@/lib/technicians/tradeBackground';

export interface TechnicianBackgroundValue {
  experienceLevel: ExperienceLevel | null;
  yearsProfessional: number | null;
}

export interface TechnicianBackgroundFieldsProps {
  value: TechnicianBackgroundValue;
  onChange: (next: TechnicianBackgroundValue) => void;
  /** Wording: "you" (self) or "this technician". */
  perspective?: 'self' | 'other';
  idPrefix?: string;
  disabled?: boolean;
}

export function TechnicianBackgroundFields({
  value, onChange, perspective = 'other', idPrefix = 'tech-bg', disabled = false,
}: TechnicianBackgroundFieldsProps) {
  const self = perspective === 'self';
  return (
    <div className="space-y-3" data-testid={`${idPrefix}-fields`}>
      <div className="space-y-1.5">
        <Label>{self ? 'Your technician experience' : 'Technician experience'} <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <p className="text-xs text-muted-foreground">
          Prior experience in the trade. It is shown as background — OptiBay still learns category strengths from real work.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Technician experience">
          {EXPERIENCE_LEVELS.map((l) => {
            const on = value.experienceLevel === l.value;
            return (
              <button
                key={l.value}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={disabled}
                data-testid={`${idPrefix}-level-${l.value}`}
                onClick={() => onChange({ ...value, experienceLevel: on ? null : l.value })}
                className={cn(
                  'rounded-md border px-3 py-2 text-left text-sm min-h-11',
                  on ? 'border-primary bg-primary/10' : 'hover:bg-muted',
                )}
              >
                <span className="font-medium">{l.label}</span>
                <span className="block text-xs text-muted-foreground">{l.hint}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="space-y-1.5 max-w-xs">
        <Label htmlFor={`${idPrefix}-years`}>
          Years of professional automotive experience <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id={`${idPrefix}-years`}
          data-testid={`${idPrefix}-years`}
          type="number"
          inputMode="numeric"
          min={0}
          max={70}
          disabled={disabled}
          value={value.yearsProfessional ?? ''}
          onChange={(e) => {
            const raw = e.target.value.trim();
            const n = raw === '' ? null : Math.max(0, Math.min(70, Math.floor(Number(raw))));
            onChange({ ...value, yearsProfessional: n == null || Number.isNaN(n) ? null : n });
          }}
          className="h-11"
        />
      </div>
    </div>
  );
}

export const EMPTY_TECHNICIAN_BACKGROUND: TechnicianBackgroundValue = Object.freeze({ experienceLevel: null, yearsProfessional: null }) as TechnicianBackgroundValue;

export const hasTechnicianBackground = (v: TechnicianBackgroundValue | null | undefined) =>
  !!v && (v.experienceLevel != null || v.yearsProfessional != null);
